'use client';

import { useState } from 'react';
import { AppShell, Group, Title, Text, Button, Stack, Burger, Drawer, Menu, ActionIcon, Tooltip } from '@mantine/core';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { IconLock, IconInfoCircle, IconPackage, IconBug, IconMail, IconChevronDown, IconZoomQuestion, IconPuzzle, IconUsers, IconSpeakerphone, IconUserCircle, IconBulb, IconShieldCheck } from '@tabler/icons-react';
import { useAuth } from '@/context/AuthContext';
import { useUser } from '@/context/UserContext';
import BricksPerBag from '@/components/BricksPerBag';
import { HeaderSearch } from '@/components/HeaderSearch';

function isCurrentPath(pathname, href) {
  if (href === '/') return pathname === '/';
  return pathname === href || pathname.startsWith(`${href}/`);
}

function MobileNavLink({ href, icon: Icon, children, accent = false, onNavigate, current = false }) {
  return (
    <Link
      href={href}
      className="mobile-nav-link"
      data-accent={accent || undefined}
      data-current={current || undefined}
      aria-current={current ? 'page' : undefined}
      onClick={onNavigate}
    >
      <Icon size={20} aria-hidden="true" focusable="false" />
      <span>{children}</span>
    </Link>
  );
}

export function AppShellLayout({ children }) {
  const pathname = usePathname();
  const { user } = useAuth();
  const { account } = useUser();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <AppShell
      header={{ height: 70 }}
    >
      <a className="skip-link" href="#main-content">Skip to main content</a>
      {/* --- DE HEADER BAR --- */}
      <AppShell.Header style={{
        border: 'none',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        boxShadow: '0 2px 12px rgba(16, 24, 40, 0.05)',
        padding: 0
      }}>
        <div className="lego-strip" style={{ position: 'absolute', bottom: 0, left: 0 }} />
        <Group h="100%" px="xl" justify="space-between" align="center">
          <Group> {/* Dit is de linker groep voor het logo */}
            <Link
              href="/"
              aria-label="Bricks Per Bag home"
              aria-current={pathname === '/' ? 'page' : undefined}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <Group gap="md" align="center">
                <BricksPerBag className="header-icon" width={52} height={52} />
                <Stack gap={2} align="flex-start">
                  <Title
                    order={4}
                    className="header-title"
                    style={{
                      lineHeight: 1,
                      fontFamily: 'var(--font-montserrat), Montserrat, sans-serif',
                      fontWeight: 800,
                      letterSpacing: '0.3px',
                      fontSize: '1.15rem',
                      color: '#1a1d21'
                    }}
                  >
                    BRICKS<span style={{ color: '#1971c2' }}>PER</span>BAG<span style={{ color: '#66707a', fontWeight: 600, fontSize: '0.75em' }}>.COM</span>
                    <span style={{ backgroundColor: '#ffcf00', color: '#1a1d21', padding: '2px 6px', borderRadius: '4px', fontSize: '9px', fontWeight: 800, marginLeft: '8px', verticalAlign: 'middle', letterSpacing: '0.5px' }}>BETA</span>
                  </Title>
                  <Text size="xs" c="dimmed" className="header-tagline" style={{ lineHeight: 1, fontWeight: 500 }}>
                    Every brick in the right bag
                  </Text>
                </Stack>
              </Group>
            </Link>
          </Group>

          {/* Desktop navigation - hidden on mobile */}
          <Group component="nav" aria-label="Primary navigation" gap="sm" className="desktop-nav">
            <HeaderSearch />
            <Button
              component={Link}
              href="/findmybag"
              aria-current={isCurrentPath(pathname, '/findmybag') ? 'page' : undefined}
              variant="subtle"
              radius="xl"
              leftSection={<IconZoomQuestion size={16} />}
              style={{
                fontWeight: 500,
                color: '#495057',
                transition: 'all 0.2s ease',
              }}
            >
              Find My Bag
            </Button>
            <Button
              component={Link}
              href="/matchparts"
              aria-current={isCurrentPath(pathname, '/matchparts') ? 'page' : undefined}
              variant="subtle"
              radius="xl"
              leftSection={<IconPuzzle size={16} />}
              className="nav-matchparts-standalone"
              style={{
                fontWeight: 500,
                color: '#495057',
                transition: 'all 0.2s ease',
              }}
            >
              Help Matching Parts
            </Button>
            <Menu shadow="md" width={200} position="bottom-end">
              <Menu.Target>
                <Button
                  variant="subtle"
                  radius="xl"
                  rightSection={<IconChevronDown size={14} />}
                  style={{
                    fontWeight: 500,
                    color: '#495057',
                    transition: 'all 0.2s ease',
                  }}
                >
                  Help
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<IconPuzzle size={16} />}
                  component={Link}
                  href="/matchparts"
                  aria-current={isCurrentPath(pathname, '/matchparts') ? 'page' : undefined}
                  className="nav-matchparts-dropdown"
                >
                  Help Matching Parts
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconUsers size={16} />}
                  component={Link}
                  href="/contribute"
                  aria-current={isCurrentPath(pathname, '/contribute') ? 'page' : undefined}
                >
                  Contribute
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconPackage size={16} />}
                  component={Link}
                  href="/request"
                  aria-current={isCurrentPath(pathname, '/request') ? 'page' : undefined}
                >
                  Request Set
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconBug size={16} />}
                  component={Link}
                  href="/report"
                  aria-current={isCurrentPath(pathname, '/report') ? 'page' : undefined}
                >
                  Report Issue
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconMail size={16} />}
                  component={Link}
                  href="/contact"
                  aria-current={isCurrentPath(pathname, '/contact') ? 'page' : undefined}
                >
                  Contact
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconSpeakerphone size={16} />}
                  component={Link}
                  href="/advertise"
                  aria-current={isCurrentPath(pathname, '/advertise') ? 'page' : undefined}
                >
                  Advertise
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconBulb size={16} />}
                  component={Link}
                  href="/mocdesigners"
                  aria-current={isCurrentPath(pathname, '/mocdesigners') ? 'page' : undefined}
                >
                  For MOC Designers
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconShieldCheck size={16} />}
                  component={Link}
                  href="/legal"
                  aria-current={isCurrentPath(pathname, '/legal') ? 'page' : undefined}
                >
                  Privacy &amp; Terms
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
            <Button
              component={Link}
              href="/about"
              aria-current={isCurrentPath(pathname, '/about') ? 'page' : undefined}
              variant="subtle"
              radius="xl"
              style={{
                fontWeight: 500,
                color: '#495057',
                transition: 'all 0.2s ease',
              }}
            >
              About
            </Button>
            {user && (
              <Button
                component={Link}
                href="/admin"
                aria-current={isCurrentPath(pathname, '/admin') ? 'page' : undefined}
                variant="light"
                leftSection={<IconLock size={16} />}
                radius="xl"
                style={{
                  fontWeight: 500,
                  boxShadow: '0 2px 8px rgba(34, 139, 230, 0.2)',
                  transition: 'all 0.2s ease',
                }}
              >
                Admin
              </Button>
            )}
            <Tooltip label={account ? 'My account' : 'Log in'} withArrow>
              <ActionIcon
                component={Link}
                href={account ? '/account' : '/account/login'}
                aria-current={isCurrentPath(pathname, account ? '/account' : '/account/login') ? 'page' : undefined}
                variant={account ? 'light' : 'subtle'}
                color={account ? 'blue' : 'gray'}
                radius="xl"
                size="lg"
                aria-label={account ? 'My account' : 'Log in'}
              >
                <IconUserCircle size={24} />
              </ActionIcon>
            </Tooltip>
          </Group>

          {/* Mobile hamburger menu - shown only on mobile */}
          <Burger
            opened={mobileMenuOpen}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="mobile-burger"
            size="sm"
            color="#495057"
            aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            style={{ alignSelf: 'center' }}
          />
        </Group>
      </AppShell.Header>

      {/* Mobile navigation drawer */}
      <Drawer
        opened={mobileMenuOpen}
        onClose={closeMobileMenu}
        position="right"
        size="xs"
        title={(
          <Link
            href="/"
            onClick={closeMobileMenu}
            className="mobile-drawer-brand"
            aria-label="Bricks Per Bag home"
            aria-current={pathname === '/' ? 'page' : undefined}
          >
            <BricksPerBag width={36} height={36} />
            <span>
              BRICKS<span className="mobile-drawer-brand-accent">PER</span>BAG
            </span>
          </Link>
        )}
        closeButtonProps={{ 'aria-label': 'Close navigation menu' }}
        styles={{
          header: {
            padding: '20px',
            borderBottom: '1px solid #f1f3f4',
          },
          body: {
            padding: 0,
            backgroundColor: '#fff',
          },
          content: {
            backgroundColor: '#fff',
          }
        }}
      >
        <Stack gap={0} className="mobile-drawer-content">
          <div className="mobile-nav-search">
            <HeaderSearch mobile onNavigate={closeMobileMenu} />
          </div>

          <Stack component="nav" aria-label="Mobile navigation" gap={0} className="mobile-nav-items">
            <MobileNavLink href="/findmybag" icon={IconZoomQuestion} accent current={isCurrentPath(pathname, '/findmybag')} onNavigate={closeMobileMenu}>
              Find My Bag
            </MobileNavLink>
            <MobileNavLink href="/matchparts" icon={IconPuzzle} accent current={isCurrentPath(pathname, '/matchparts')} onNavigate={closeMobileMenu}>
              Help Matching Parts
            </MobileNavLink>
            <MobileNavLink href="/request" icon={IconPackage} current={isCurrentPath(pathname, '/request')} onNavigate={closeMobileMenu}>
              Request Set
            </MobileNavLink>
            <MobileNavLink href="/report" icon={IconBug} current={isCurrentPath(pathname, '/report')} onNavigate={closeMobileMenu}>
              Report Issue
            </MobileNavLink>
            <MobileNavLink href="/contact" icon={IconMail} current={isCurrentPath(pathname, '/contact')} onNavigate={closeMobileMenu}>
              Contact
            </MobileNavLink>
            <MobileNavLink href="/contribute" icon={IconUsers} current={isCurrentPath(pathname, '/contribute')} onNavigate={closeMobileMenu}>
              Contribute
            </MobileNavLink>
            <MobileNavLink href="/about" icon={IconInfoCircle} current={isCurrentPath(pathname, '/about')} onNavigate={closeMobileMenu}>
              About
            </MobileNavLink>
            <MobileNavLink href="/advertise" icon={IconSpeakerphone} current={isCurrentPath(pathname, '/advertise')} onNavigate={closeMobileMenu}>
              Advertise
            </MobileNavLink>
            <MobileNavLink href="/mocdesigners" icon={IconBulb} current={isCurrentPath(pathname, '/mocdesigners')} onNavigate={closeMobileMenu}>
              For MOC Designers
            </MobileNavLink>
            <MobileNavLink href="/legal" icon={IconShieldCheck} current={isCurrentPath(pathname, '/legal')} onNavigate={closeMobileMenu}>
              Privacy &amp; Terms
            </MobileNavLink>
            <MobileNavLink
              href={account ? '/account' : '/account/login'}
              icon={IconUserCircle}
              accent
              current={isCurrentPath(pathname, account ? '/account' : '/account/login')}
              onNavigate={closeMobileMenu}
            >
              {account ? 'My Account' : 'Log In'}
            </MobileNavLink>
            {user && (
              <MobileNavLink href="/admin" icon={IconLock} accent current={isCurrentPath(pathname, '/admin')} onNavigate={closeMobileMenu}>
                Admin
              </MobileNavLink>
            )}
          </Stack>

          {/* Footer */}
          <Group
            style={{
              padding: '16px 20px',
              borderTop: '1px solid #f1f3f4',
              marginTop: 'auto',
            }}
          >
            <Text size="xs" c="dimmed">Every brick in the right bag</Text>
          </Group>
        </Stack>
      </Drawer>

      {/* --- DE HOOFDCONTENT VAN JE PAGINA --- */}
      <AppShell.Main id="main-content" tabIndex={-1} className="site-main" style={{ backgroundColor: 'var(--bpb-bg)' }}>
        <div className="site-content">{children}</div>

        {/* Gewone footer onderaan de pagina (niet vast in beeld: kostte op mobiel ~55px per scherm) */}
        <footer className="site-footer">
          <Text size="sm" c="dimmed" fw={600}>
            Bricksperbag.com &copy; {new Date().getFullYear()}
          </Text>
          <nav aria-label="Footer navigation" className="site-footer-nav">
            <Link href="/about" className="site-footer-link">About</Link>
            <Link href="/contact" className="site-footer-link">Contact</Link>
            <Link href="/request" className="site-footer-link">Request Set</Link>
            <Link href="/mocdesigners" className="site-footer-link">MOC Designers</Link>
            <Link href="/advertise" className="site-footer-link">Advertise</Link>
            <Link href="/legal" className="site-footer-link">Privacy &amp; Terms</Link>
          </nav>
          <Text size="sm" c="dimmed" fw={500} className="site-footer-credit">
            Made by Menno
          </Text>
        </footer>
      </AppShell.Main>
    </AppShell>
  );
}
